<template>
  <div>
    <loading-indicator v-if="isLoading" />

    <p v-if="message !== null">
      {{message}}
    </p>

    <player-mesas-table
      v-if="isMesasTableVisible"
      :mesas="mesas"
      @mesa-finish-requested="finishMesaWithConfirmation"
    />
  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { MESA_STATE_FINISHED_IGNORED } from '../model/mesa.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { getProtoAlias } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import PlayerMesasTable from './PlayerMesasTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'PlayerMesas',

  components: {
    LoadingIndicator,
    PlayerMesasTable,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      loadingStatus: LOADING_STATUS__INIT,
      mesas: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const mesas = this.mesas;
      if(mesas !== null && mesas.length === 0) {
        return 'No mesas.';
      }

      return null;
    },

    /** @returns {boolean} */
    isMesasTableVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const mesas = this.mesas;
      if(mesas === null) {
        return false;
      }

      return mesas.length > 0;
    },
  },

  created() {
    this.loadMesas();
  },

  methods: {
    loadMesas() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/player/${this.playerId}/mesas`;

      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.mesas = data;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    finishMesaWithConfirmation(mesa) {
      notifier.deleteSenderNotifications(this);

      this.confirmMesaFinish(
        mesa,
        () => {
          this.finishMesa(mesa);
        },
      );
    },

    /**
     * @param {Object} mesa
     * @param {ConfirmHandle} onConfirmed
     */
    confirmMesaFinish(mesa, onConfirmed) {
      const playerId = this.playerId;
      const mesaProtoName = getProtoAlias(mesa.protoId);

      const messageTemplate = `Are you sure you want to finish mesa (${mesaProtoName}) for player ${playerId} {env}?`;

      confirmAction(
        this,
        messageTemplate,
        'Finish',
        onConfirmed,
      );
    },

    finishMesa(mesa) {
      const playerId = this.playerId;
      const url = `/player/${playerId}/finalize_mesa`;

      const data = new FormData();
      data.set('proto_id', mesa.protoId);

      HTTP.post(url, data)
      .then((response) => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        mesa.finishStamp = data.ftime;  
        mesa.state = MESA_STATE_FINISHED_IGNORED;

        notifier.showSuccess('Mesa finished.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};
</script>
